import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Loader2, Target, CheckCircle2, AlertTriangle, Scale } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VARIABLES = ['purchase_price', 'rent', 'down_payment', 'interest_rate'];

const fmtVal = (solveFor, v) => {
  if (v === null || v === undefined) return '—';
  if (solveFor === 'purchase_price') return `$${Math.round(v).toLocaleString()}`;
  if (solveFor === 'rent') return `$${Math.round(v / 12).toLocaleString()}/mo`;
  if (solveFor === 'down_payment') return `${v.toFixed(1)}%`;
  if (solveFor === 'interest_rate') return `${v.toFixed(2)}%`;
  return String(v);
};

const labelKey = (v) => ({
  purchase_price: 'breakEven.purchasePrice',
  rent: 'breakEven.rent',
  down_payment: 'breakEven.downPayment',
  interest_rate: 'breakEven.interestRate',
}[v]);

const Card = ({ solveFor, result, scenario }) => {
  const { t } = useTranslation();
  if (!result) {
    return (
      <div data-testid={`be-card-${solveFor}`}
           className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#0F1419] p-4 min-h-[140px] flex items-center justify-center">
        <span className="text-xs text-slate-400">—</span>
      </div>
    );
  }
  const isImpossible = result.status === 'impossible' || result.status === 'out_of_range';
  const isAlreadyMet = result.status === 'already_met';
  const isSolved = result.status === 'solved';

  const Icon = isSolved ? Target : isAlreadyMet ? CheckCircle2 : AlertTriangle;
  const badgeCls = isSolved
    ? 'text-violet-600 dark:text-violet-400'
    : isAlreadyMet
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-amber-600 dark:text-amber-400';

  return (
    <div data-testid={`be-card-${solveFor}`}
         className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${badgeCls}`} />
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
          {t(labelKey(solveFor))}
        </p>
      </div>

      {isSolved && (
        <>
          <p data-testid={`be-value-${solveFor}`}
             className="font-primary font-black text-2xl text-slate-900 dark:text-slate-100 tabular-nums">
            {fmtVal(solveFor, result.solved_value)}
          </p>
          {solveFor === 'rent' && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
              {t('breakEven.annualEquiv', { amount: `$${Math.round(result.solved_value).toLocaleString()}` })}
            </p>
          )}
          {solveFor === 'down_payment' && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
              {t('breakEven.dpDollarLtv', {
                dollars: `$${Math.round((scenario.purchase_price || 0) * (result.solved_value / 100)).toLocaleString()}`,
                ltv: `${(100 - result.solved_value).toFixed(1)}%`,
              })}
            </p>
          )}
          <p className="text-[10px] text-slate-400 mt-2">
            {t('breakEven.approxZero')}
          </p>
        </>
      )}
      {isAlreadyMet && (
        <p className="text-sm text-emerald-700 dark:text-emerald-300">{t('breakEven.alreadyZero')}</p>
      )}
      {isImpossible && (
        <p className="text-sm text-amber-700 dark:text-amber-300">{t('breakEven.impossible')}</p>
      )}
    </div>
  );
};

const BreakEvenPanel = ({ scenario, locks }) => {
  const { t } = useTranslation();
  const [results, setResults] = useState({ purchase_price: null, rent: null, down_payment: null, interest_rate: null });
  const [loading, setLoading] = useState(false);
  const [computedFor, setComputedFor] = useState(null); // stringified scenario hash

  const scenarioKey = JSON.stringify({
    p: scenario.purchase_price, r: scenario.annual_income, dp: scenario.down_payment_percent,
    ir: scenario.interest_rate, t: scenario.annual_property_tax, i: scenario.annual_insurance,
    m: scenario.monthly_maintenance, v: scenario.vacancy_rate, pm: scenario.property_management_percent,
  });

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const requests = VARIABLES.map((v) =>
        axios.post(
          `${BACKEND_URL}/api/scenario/solve`,
          { scenario, solve_for: v, target_monthly_cash_flow: 0, locked: [] },
          { withCredentials: true }
        ).then((r) => [v, r.data]).catch(() => [v, { status: 'out_of_range', solved_value: null }])
      );
      const settled = await Promise.all(requests);
      const next = { purchase_price: null, rent: null, down_payment: null, interest_rate: null };
      for (const [v, data] of settled) next[v] = data;
      setResults(next);
      setComputedFor(scenarioKey);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioKey]);

  const stale = computedFor && computedFor !== scenarioKey;

  return (
    <section
      data-testid="break-even-section"
      role="region"
      aria-live="polite"
      aria-atomic="false"
      className="mt-8 bg-white dark:bg-[#131824] rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-800"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-violet-500" />
            <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100">
              {t('breakEven.title')}
            </h2>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('breakEven.subtitle')}
          </p>
        </div>
        <Button
          data-testid="break-even-compute-btn"
          onClick={run}
          disabled={loading}
          size="sm"
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          {loading ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />{t('breakEven.computing')}</>)
                   : (computedFor ? t('breakEven.recompute') : t('breakEven.compute'))}
        </Button>
      </div>

      {stale && (
        <p data-testid="break-even-stale" className="text-[11px] text-amber-600 dark:text-amber-400 mb-3">
          {t('breakEven.stale')}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {VARIABLES.map((v) => (
          <Card key={v} solveFor={v} result={results[v]} scenario={scenario} />
        ))}
      </div>
    </section>
  );
};

export default BreakEvenPanel;
